import React from 'react';
import { Check, Droplet, Dumbbell, TrendingUp, Wheat } from 'lucide-react';
import { num0 } from '../../lib/numeros';
import { COLOR_MACRO } from './MacrosHoy';

/*
 * TU PROGRESO DE HOY (rediseño del Inicio, 15-10). Lo CONSUMIDO frente al objetivo del día,
 * una fila por macro con su barra. «Llevas» es lo de siempre (useDiaDeHoy: comidas marcadas
 * + extras), nunca lo planificado: aquí no se hace ninguna cuenta nueva.
 *
 * Si te pasas, la barra no crece más allá del ancho: representa lo que llevas, el tramo
 * hasta el objetivo va en el color del macro y lo que sobra en ámbar a rayas.
 */

const MACROS = [
    { m: 'P', nombre: 'Proteína', Icono: Dumbbell },
    { m: 'H', nombre: 'Carbohidratos', Icono: Wheat },
    { m: 'G', nombre: 'Grasas', Icono: Droplet },
];

const AMBAR = '#F59E0B';

const FilaMacro = ({ m, nombre, Icono, llevas, objetivo }) => {
    const color = COLOR_MACRO[m];
    const meta = Math.max(0, objetivo?.[m] || 0);
    const lleva = Math.max(0, llevas?.[m] || 0);
    const resta = meta - lleva;
    const cuadra = meta > 0 && Math.abs(resta) < 0.5;
    const pasado = resta <= -0.5;
    // Con exceso, la barra entera es «lo que llevas»: el objetivo ocupa meta/lleva de ella.
    const relleno = pasado ? 100 : meta > 0 ? Math.min(100, (lleva / meta) * 100) : 0;
    const tramoObjetivo = pasado ? (meta / lleva) * 100 : relleno;

    return (
        <div className="flex items-center gap-3" data-testid={`macro-${m}`}>
            <span className="w-11 h-11 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{ borderColor: color, backgroundColor: `${color}14`, color }}>
                <Icono className="w-5 h-5" strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-tight">{nombre}</p>
                        <p className="leading-tight mt-0.5">
                            <span className="font-heading text-xl font-bold text-foreground">{num0(lleva)}</span>
                            <span className="text-sm text-muted-foreground"> / {num0(meta)} g</span>
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        {cuadra ? (
                            <p className="flex items-center gap-1 text-sm font-semibold text-emerald-400">
                                <Check className="w-4 h-4" strokeWidth={3} /> Cumplido
                            </p>
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground leading-tight">{pasado ? 'Te pasas' : 'Faltan'}</p>
                                <p className="text-sm font-semibold leading-tight mt-0.5"
                                    style={{ color: pasado ? AMBAR : undefined }}>
                                    {num0(Math.abs(resta))} g
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="relative h-2 rounded-full bg-white/10 overflow-hidden mt-2" role="progressbar"
                    aria-label={nombre} aria-valuemin={0} aria-valuemax={Math.round(meta)} aria-valuenow={Math.round(lleva)}>
                    <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                        style={{ width: `${tramoObjetivo}%`, backgroundColor: color }} />
                    {pasado && (
                        <div className="absolute inset-y-0 right-0 rounded-r-full"
                            style={{
                                left: `${tramoObjetivo}%`,
                                backgroundImage: `repeating-linear-gradient(135deg, ${AMBAR} 0 4px, ${AMBAR}80 4px 8px)`,
                            }} />
                    )}
                </div>
            </div>
        </div>
    );
};

const ProgresoHoy = ({ llevas, objetivo }) => (
    // `tu-dieta-hoy` es el ancla a la que sube la pantalla al marcar una comida (NutricionHoy).
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 scroll-mt-20 lg:scroll-mt-6"
        data-testid="tu-dieta-hoy">
        <header className="flex items-start gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-brand flex-shrink-0 mt-0.5" strokeWidth={2.5} />
            <h2 className="font-heading text-xl font-bold text-foreground leading-tight">Tus macros de hoy</h2>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {MACROS.map((x) => <FilaMacro key={x.m} {...x} llevas={llevas} objetivo={objetivo} />)}
        </div>
    </section>
);

export default ProgresoHoy;
